import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage.js";
import AuthPage from "@/pages/AuthPage.js";
import ChatPage from "@/pages/ChatPage.js";
import PricingPage from "@/pages/PricingPage.js";
import BillingPage from "@/pages/BillingPage.js";
import SettingsPage from "@/pages/SettingsPage.js";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage.js";
import AdminPage from "@/pages/AdminPage.js";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage.js";
import TenantDirectoryPage from "@/pages/TenantDirectoryPage.js";
import RequestAccessPage from "@/pages/RequestAccessPage.js";
import OperationsDashboard from "@/pages/operations/OperationsDashboard.js";
import IncidentsPage from "@/pages/operations/IncidentsPage.js";
import IncidentDetailPage from "@/pages/operations/IncidentDetailPage.js";
import KnowledgeReviewPage from "@/pages/operations/KnowledgeReviewPage.js";
import ExecutiveDashboard from "@/pages/operations/ExecutiveDashboard.js";
import PilotHealthPage from "@/pages/operations/PilotHealthPage.js";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback.js";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage.js";
import ResetPasswordPage from "@/pages/ResetPasswordPage.js";
import { readBody, fetchWithReadRetry } from "@/lib/http";
import { authStore } from "@/lib/authStore";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => authStore.getAccessToken());
  const [loading, setLoading] = useState(true);

  // Subscribe to authStore so refresh-driven token rotations propagate to UI.
  useEffect(() => {
    const unsub = authStore.subscribe((newToken) => setToken(newToken));
    return unsub;
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // 1. Try the in-memory / legacy localStorage access token first.
      let storedToken = authStore.getAccessToken();
      // 2. If nothing in store but a csrf cookie is set (hardened mode after
      //    a page reload) attempt a silent refresh to restore the session.
      if (!storedToken && authStore.getCsrfToken()) {
        const ok = await authStore.refreshSilently();
        if (ok) storedToken = authStore.getAccessToken();
      }
      if (storedToken) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          if (response.ok) {
            const userData = await readBody(response);
            setUser(userData);
            setToken(storedToken);
          } else if (response.status === 401 && authStore.isHardened()) {
            // Access token expired but we may still be able to refresh
            const ok = await authStore.refreshSilently();
            if (ok) {
              const retried = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
                headers: { 'Authorization': `Bearer ${authStore.getAccessToken()}` }
              });
              if (retried.ok) {
                setUser(await readBody(retried));
                setToken(authStore.getAccessToken());
              } else {
                authStore.clear();
                setToken(null);
              }
            } else {
              authStore.clear();
              setToken(null);
            }
          } else {
            authStore.clear();
            setToken(null);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          authStore.clear();
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const _applyAuthResponse = (data) => {
    if (!data || !data.access_token) {
      throw new Error('Auth response missing access_token');
    }
    // authStore picks "hardened" mode automatically when csrf_token is present.
    authStore.setSession({
      access_token: data.access_token,
      csrf_token: data.csrf_token,
    });
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const login = async (email, password) => {
    // Use fetchWithReadRetry — single connection drop on the login call
    // shouldn't fail the user (most common cause: keep-alive socket closure).
    const result = await fetchWithReadRetry(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      }
    );

    if (!result.ok) {
      throw new Error((result.body && result.body.detail) || 'Login failed');
    }
    return _applyAuthResponse(result.body);
  };

  const register = async (name, email, password) => {
    const result = await fetchWithReadRetry(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/register`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      }
    );

    if (!result.ok) {
      throw new Error((result.body && result.body.detail) || 'Registration failed');
    }
    return _applyAuthResponse(result.body);
  };

  const loginWithGoogleSession = async (sessionId) => {
    const result = await fetchWithReadRetry(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/callback`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      }
    );
    if (!result.ok) {
      throw new Error((result.body && result.body.detail) || 'Google sign-in failed');
    }
    return _applyAuthResponse(result.body);
  };

  const logout = async () => {
    // Best-effort backend logout (revokes refresh family + clears cookies).
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        });
    } catch (_) { /* network failure is fine — UI must still log out */ }
    authStore.clear();
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const userData = await readBody(response);
          setUser(userData);
        }
      } catch (error) {
        console.error('Refresh user error:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, loginWithGoogleSession }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-subtle text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/callback" element={<GoogleAuthCallback />} />
            <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset" element={<ResetPasswordPage />} />
            <Route path="/request-access" element={<RequestAccessPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route 
              path="/chat" 
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chat/:conversationId" 
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/billing" 
              element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/knowledge-base" 
              element={
                <ProtectedRoute>
                  <KnowledgeBasePage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/tenants"
              element={
                <ProtectedRoute>
                  <TenantDirectoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations"
              element={<ProtectedRoute><OperationsDashboard /></ProtectedRoute>}
            />
            <Route
              path="/operations/incidents"
              element={<ProtectedRoute><IncidentsPage /></ProtectedRoute>}
            />
            <Route
              path="/operations/incidents/:id"
              element={<ProtectedRoute><IncidentDetailPage /></ProtectedRoute>}
            />
            <Route
              path="/operations/knowledge"
              element={<ProtectedRoute><KnowledgeReviewPage /></ProtectedRoute>}
            />
            <Route
              path="/operations/executive"
              element={<ProtectedRoute><ExecutiveDashboard /></ProtectedRoute>}
            />
            <Route
              path="/operations/pilot-health"
              element={<ProtectedRoute><PilotHealthPage /></ProtectedRoute>}
            />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
